import { ApolloError } from 'apollo-client';

import { useGetCollectionQuery, GqlProducts } from 'types/graphql';
import useProductQueryParams from 'src/hooks/use-product-query-params';

import { parseProduct } from 'utils/helpers/product';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';

type UseCollectionProps = {
  slug: string;
  dispensaryId: string;
  menuType: string;
  limit?: number;
  offset?: number;
};

type CollectionWithProductsType = {
  id: string;
  title: string;
  description?: string;
  image?: string;
  customCategory?: boolean;
  enabled?: boolean;
  products: GqlProducts[];
  productCount: number;
};

type UseCollectionReturn = {
  collection?: CollectionWithProductsType;
  loading: boolean;
  error?: ApolloError;
};

export function useCollections({ slug, dispensaryId, limit = 30 }: UseCollectionProps): UseCollectionReturn {
  const { queryParams: qp } = useProductQueryParams();
  const hasBrandsFilter = !!qp.brands.length;

  const { data: collectionData, loading: collectionLoading, error: collectionError } = useGetCollectionQuery({
    skip: !dispensaryId,
    variables: {
      collectionId: slug,
      dispensaryId,
    },
  });

  const collectionInfo = collectionData?.getCollection ?? null;
  const collectionId = collectionInfo?.id;

  const { products, loading: productsLoading, error: productsError, totalCount } = useDispensaryProducts({
    filters: { collectionId },
    perPage: limit,
    sortBy: !hasBrandsFilter ? qp.sortby : undefined,
    skip: !collectionId,
  });

  const collection = {
    id: collectionInfo?.id ?? '',
    title: collectionInfo?.title ?? '',
    description: collectionInfo?.description ?? '',
    image: collectionInfo?.image ?? '',
    customCategory: collectionInfo?.customCategory ?? false,
    enabled: collectionInfo?.enabled ?? false,
    products: products.map((product) => parseProduct(product)),
    productCount: totalCount,
  };

  const loading = productsLoading || collectionLoading;
  const error = productsError ?? collectionError;

  return {
    collection,
    loading,
    error,
  };
}
